<script setup>
// Dados da CONTRATADA (quem presta o serviço e emite a nota). Fica na conta e
// é copiado para cada contrato novo na geração — contrato já emitido não muda.
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ContractsAPI from 'dashboard/api/contracts';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['saved']);

const { t } = useI18n();

// Ordem de leitura de um cadastro: quem é, onde fica, como falar, quem assina.
const LAYOUT = [
  { key: 'razao_social', span: 'col-span-2' },
  { key: 'cnpj', span: 'col-span-2 sm:col-span-1' },
  { key: 'telefone', span: 'col-span-2 sm:col-span-1' },
  { key: 'endereco', span: 'col-span-2' },
  { key: 'bairro', span: 'col-span-2 sm:col-span-1' },
  { key: 'cep', span: 'col-span-2 sm:col-span-1' },
  { key: 'cidade', span: 'col-span-2 sm:col-span-1' },
  { key: 'uf', span: 'col-span-2 sm:col-span-1' },
  { key: 'email', span: 'col-span-2' },
  { key: 'representante_nome', span: 'col-span-2 sm:col-span-1' },
  { key: 'representante_cargo', span: 'col-span-2 sm:col-span-1' },
  { key: 'foro', span: 'col-span-2' },
];

const form = ref(Object.fromEntries(LAYOUT.map(({ key }) => [key, ''])));
const invalid = ref([]);
const isSaving = ref(false);

onMounted(async () => {
  try {
    const { data } = await ContractsAPI.issuer();
    form.value = { ...form.value, ...data };
  } catch {
    // Formulário vazio já serve para o primeiro cadastro.
  }
});

const save = async () => {
  isSaving.value = true;
  invalid.value = [];
  try {
    const { data } = await ContractsAPI.updateIssuer(form.value);
    form.value = { ...form.value, ...data };
    useAlert(t('DEALS.CONTRACTS.ISSUER.SAVED'));
    emit('saved');
  } catch (error) {
    invalid.value = error?.response?.data?.invalid_fields || [];
    if (!invalid.value.length) useAlert(t('DEALS.CONTRACTS.ALERTS.ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <form
    class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
    @submit.prevent="save"
  >
    <h4 class="text-sm font-semibold text-n-slate-12">
      {{ t('DEALS.CONTRACTS.ISSUER.TITLE') }}
    </h4>
    <p class="mt-1 mb-4 text-xs leading-relaxed text-n-slate-11">
      {{ t('DEALS.CONTRACTS.ISSUER.HINT') }}
    </p>
    <div class="grid grid-cols-2 gap-3">
      <Input
        v-for="field in LAYOUT"
        :key="field.key"
        v-model="form[field.key]"
        :class="field.span"
        size="sm"
        :label="t(`DEALS.CONTRACTS.ISSUER.FIELDS.${field.key}`)"
        :message="
          invalid.includes(field.key) ? t('DEALS.CONTRACTS.FORM.INVALID') : ''
        "
        :message-type="invalid.includes(field.key) ? 'error' : 'info'"
      />
    </div>
    <div class="mt-4 flex justify-end">
      <Button
        type="submit"
        size="sm"
        :label="t('DEALS.CONTRACTS.ISSUER.SAVE')"
        :is-loading="isSaving"
      />
    </div>
  </form>
</template>
